<template>
  <div class="container py-4">
    <div class="page-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-0 section-title-accent">Promotions</h4>
          <p class="small text-muted mb-0 mt-1">
            Discount codes cashiers enter at POS checkout.
          </p>
        </div>
        <icon-button
          :func="() => openCreate()"
          :icon="'plus'"
          :title="'Add promotion'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Add promotion'"
        />
      </div>

      <b-table
        :items="promotions"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
        show-empty
        empty-text="No promotions yet"
      >
        <template #cell(code)="data">
          <code class="promo-code">{{ data.value }}</code>
        </template>
        <template #cell(type)="data">
          {{ typeLabel(data.value) }}
        </template>
        <template #cell(value)="data">
          {{ formatValue(data.item) }}
        </template>
        <template #cell(dates)="data">
          <span class="small text-muted">{{ formatDates(data.item) }}</span>
        </template>
        <template #cell(isActive)="data">
          <b-badge :class="statusBadge(data.value)">
            {{ data.value ? "Active" : "Inactive" }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <icon-button
            :func="() => openEdit(data.item)"
            :icon="'edit'"
            :title="'Edit'"
            :classes="'akkurate-warning btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="showForm"
      :title="editingId ? 'Edit promotion' : 'New promotion'"
      size="lg"
      @ok="save"
    >
      <b-form-group label="Code">
        <b-form-input
          class="form-control"
          v-model="form.code"
          maxlength="32"
          placeholder="e.g. SUMMER10"
          :disabled="!!editingId"
        />
        <b-form-text
          >Uppercase at checkout. Cannot change after create.</b-form-text
        >
      </b-form-group>
      <b-form-group label="Display name">
        <b-form-input
          class="form-control"
          v-model="form.name"
          maxlength="128"
          placeholder="Summer sale 10%"
        />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Discount type">
            <b-form-select
              v-model="form.type"
              class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
              :options="typeOptions"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="valueLabel">
            <b-form-input
              class="form-control"
              v-model.number="form.value"
              type="number"
              min="0"
              :step="form.type === 'fixed_off' ? '0.01' : '1'"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group
        v-if="form.type === 'category_percent'"
        label="Product category"
      >
        <b-form-input
          class="form-control"
          v-model="form.category"
          maxlength="64"
          placeholder="Must match product category exactly"
        />
      </b-form-group>
      <b-form-group label="Minimum purchase (optional)">
        <b-form-input
          class="form-control"
          v-model.number="form.minPurchase"
          type="number"
          min="0"
          step="0.01"
          placeholder="Leave blank for no minimum"
        />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Starts (optional)">
            <b-form-input
              class="form-control"
              v-model="form.startsAt"
              type="datetime-local"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Ends (optional)">
            <b-form-input
              class="form-control"
              v-model="form.endsAt"
              type="datetime-local"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <b-form-checkbox v-model="form.isActive">Active</b-form-checkbox>
      </b-form-group>
      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Save'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save'"
          :disabled="saving"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";

const TYPE_OPTIONS = [
  { value: "percent_off", text: "Percent off entire order" },
  { value: "fixed_off", text: "Fixed amount off order" },
  { value: "category_percent", text: "Percent off category items" },
];

const EMPTY_FORM = () => ({
  code: "",
  name: "",
  type: "percent_off",
  value: 0,
  category: "",
  minPurchase: null,
  startsAt: "",
  endsAt: "",
  isActive: true,
});

export default {
  name: "Promotions",
  components: { IconButton },
  data() {
    return {
      promotions: [],
      loading: false,
      saving: false,
      showForm: false,
      editingId: null,
      form: EMPTY_FORM(),
      typeOptions: TYPE_OPTIONS,
      fields: [
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "type", label: "Type" },
        { key: "value", label: "Value" },
        { key: "dates", label: "Schedule" },
        { key: "isActive", label: "Status" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId"]),
    valueLabel() {
      if (this.form.type === "fixed_off") return "Amount off";
      return "Percent off";
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    typeLabel(type) {
      return TYPE_OPTIONS.find((o) => o.value === type)?.text || type;
    },
    formatValue(item) {
      if (item.type === "fixed_off") return this.formatMoney(item.value);
      if (item.type === "category_percent") {
        return `${item.value}%${item.category ? ` · ${item.category}` : ""}`;
      }
      return `${item.value}%`;
    },
    formatDates(item) {
      const start = item.startsAt ? this.formatDate(item.startsAt) : null;
      const end = item.endsAt ? this.formatDate(item.endsAt) : null;
      if (start && end) return `${start} → ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return "Always";
    },
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
    toLocalInput(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    toApiDate(local) {
      if (!local) return null;
      return new Date(local).toISOString();
    },
    openCreate() {
      this.editingId = null;
      this.form = EMPTY_FORM();
      this.showForm = true;
    },
    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        code: item.code,
        name: item.name,
        type: item.type,
        value: item.value,
        category: item.category || "",
        minPurchase: item.minPurchase,
        startsAt: this.toLocalInput(item.startsAt),
        endsAt: this.toLocalInput(item.endsAt),
        isActive: item.isActive,
      };
      this.showForm = true;
    },
    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.selectedStoreId) params.storeId = this.selectedStoreId;
        this.promotions = await ApiService.get("promotions", params);
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not load promotions",
          icon: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    async save(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.form.code.trim() || !this.form.name.trim()) {
        toast.fire({ title: "Code and name are required", icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          storeId: this.selectedStoreId || null,
          code: this.form.code.trim(),
          name: this.form.name.trim(),
          type: this.form.type,
          value: Number(this.form.value) || 0,
          category:
            this.form.type === "category_percent"
              ? this.form.category.trim() || null
              : null,
          minPurchase: this.form.minPurchase || null,
          startsAt: this.toApiDate(this.form.startsAt),
          endsAt: this.toApiDate(this.form.endsAt),
          isActive: this.form.isActive,
        };
        if (this.editingId) {
          await ApiService.put(`promotions/${this.editingId}`, payload);
        } else {
          await ApiService.post("promotions", payload);
        }
        this.showForm = false;
        await this.load();
        toast.fire({ title: "Promotion saved", icon: "success" });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not save promotion",
          icon: "danger",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.promo-code {
  font-size: 0.85rem;
  background: #f1f5f9;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}
</style>
